<template>
    <UiModal
        :isOpen="show"
        @close="$emit('close')"
        :title="isEditing ? 'Edit product change' : 'Add product change'"
        :description="modalSubTitle"
    >
        <template #content>
            <div class="bg-white dark:bg-slate-800 rounded-lg w-full max-w-3xl mx-auto">
                <div class="flex flex-col items-start mt-1 gap-8 w-full">
                    <div
                        v-show="currentStep === 1"
                        class="flex flex-col items-start self-stretch"
                    >
                        <label
                            class="flex pb-2 items-start gap-2.5 self-stretch text-slate-600 dark:text-slate-300 text-sm font-medium leading-5"
                        >
                            Product change type
                        </label>
                        <div
                            class="flex flex-col md:flex-row items-stretch gap-4 self-stretch"
                        >
                            <div
                                class="flex p-3 justify-between items-start flex-1 rounded-lg border-2 bg-white dark:bg-slate-800 cursor-pointer relative radio-card"
                                :class="{
                                    'border-red-500':
                                        selectedOption === 'add-previous',
                                    'border-slate-200 dark:border-slate-700':
                                        selectedOption !== 'add-previous',
                                }"
                                @click="selectedOption = 'add-previous'"
                            >
                                <input
                                    type="radio"
                                    class="absolute top-3 right-3 form-radio cursor-pointer"
                                    name="productChangeType"
                                    value="add-previous"
                                    :checked="selectedOption === 'add-previous'"
                                    @change="selectedOption = 'add-previous'"
                                />
                                <div
                                    class="flex flex-col items-start gap-2 pr-8"
                                >
                                    <div class="flex items-center gap-2">
                                        <WalletIcon
                                            icon="generic-card"
                                            :width="32"
                                            :height="20"
                                            class="rounded-sm"
                                        />

                                        <Icon
                                            icon="arrow-right-alt"
                                            class="text-slate-500 dark:text-slate-400"
                                        />
                                        <img
                                            :src="`${cdnUrl}/${cardData.card.card_image.path}/_small/${cardData.card.card_image.file_name}`"
                                            v-if="
                                                cardData &&
                                                cardData.card &&
                                                cardData.card.card_image
                                            "
                                            class="w-8 h-5 object-cover rounded"
                                        />
                                        <img
                                            :src="cardImageUrl"
                                            alt="Current Card"
                                            class="w-8 h-5 object-cover rounded"
                                            v-else-if="
                                                cardImageUrl &&
                                                cardImageUrl.length > 0
                                            "
                                        />
                                        <div
                                            class="w-8 h-5 bg-slate-200 rounded flex items-center justify-center text-xs text-slate-500 dark:text-slate-400"
                                            v-else
                                        >
                                            {{ cardName.substring(0, 4) }}
                                        </div>
                                    </div>
                                    <p
                                        class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                    >
                                        Add a previous card
                                    </p>
                                    <p
                                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                                    >
                                        For tracking purposes. Use if you've
                                        already completed a product change.
                                    </p>
                                </div>
                            </div>

                            <div
                                class="flex p-3 justify-between items-start flex-1 rounded-lg border-2 bg-white dark:bg-slate-800 cursor-pointer relative radio-card"
                                :class="{
                                    'border-red-500':
                                        selectedOption === 'change-to-new',
                                    'border-slate-200 dark:border-slate-700':
                                        selectedOption !== 'change-to-new',
                                }"
                                @click="selectedOption = 'change-to-new'"
                            >
                                <input
                                    type="radio"
                                    class="absolute top-3 right-3 form-radio cursor-pointer"
                                    name="productChangeType"
                                    value="change-to-new"
                                    :checked="
                                        selectedOption === 'change-to-new'
                                    "
                                    @change="selectedOption = 'change-to-new'"
                                />
                                <div
                                    class="flex flex-col items-start gap-2 pr-8"
                                >
                                    <div class="flex items-center gap-2">
                                        <img
                                            :src="`${cdnUrl}/${cardData.card.card_image.path}/_small/${cardData.card.card_image.file_name}`"
                                            v-if="
                                                cardData &&
                                                cardData.card &&
                                                cardData.card.card_image
                                            "
                                            class="w-8 h-5 object-cover rounded"
                                        />
                                        <img
                                            :src="cardImageUrl"
                                            alt="Current Card"
                                            class="w-8 h-5 object-cover rounded"
                                            v-else-if="
                                                cardImageUrl &&
                                                cardImageUrl.length > 0
                                            "
                                        />
                                        <div
                                            class="w-8 h-5 bg-slate-200 rounded flex items-center justify-center text-xs text-slate-500 dark:text-slate-400"
                                            v-else
                                        >
                                            {{ cardName.substring(0, 4) }}
                                        </div>
                                        <Icon
                                            icon="arrow-right-alt"
                                            class="text-slate-500 dark:text-slate-400"
                                        />
                                        <WalletIcon
                                            icon="generic-card"
                                            :width="32"
                                            :height="20"
                                            class="rounded-sm"
                                        />
                                    </div>

                                    <p
                                        class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                    >
                                        Change card to a new one
                                    </p>
                                    <p
                                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                                    >
                                        For completing a product change now.
                                        We'll guide you through the process.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- step 2, add previous card: -->
                    <div
                        v-show="
                            currentStep === 2 &&
                            selectedOption === 'add-previous'
                        "
                        class="flex flex-col items-start gap-8 self-stretch w-full"
                    >
                        <div class="flex items-center gap-3 self-stretch mt-2">
                            <div
                                v-if="
                                    productChangeData.previous_card &&
                                    productChangeData.previous_card.card_image
                                "
                                class="w-[82px] h-[50px] rounded overflow-hidden border-2 border-red-500"
                                :style="`background: url('${cdnUrl}/${productChangeData.previous_card.card_image.path}/_small/${productChangeData.previous_card.card_image.file_name}') lightgray 50% / cover no-repeat;`"
                            ></div>
                            <WalletIcon
                                v-else
                                icon="generic-card"
                                :width="82"
                                :height="50"
                                class="border-2 border-red-500 rounded"
                            />

                            <div
                                v-if="selectedOption === 'add-previous'"
                                class="flex w-6 h-6 justify-center items-center"
                            >
                                <Icon
                                    icon="arrow-right-alt"
                                    class="w-6 h-6 text-slate-500 dark:text-slate-400"
                                />
                            </div>

                            <div
                                class="flex h-[50px] w-[80px] items-center justify-center rounded overflow-hidden"
                                :style="
                                    hasChangedToCardImage
                                        ? `background: url('${changedToCardImage}') lightgray 50% / cover no-repeat;`
                                        : 'background: lightgray;'
                                "
                            >
                                <span
                                    v-if="!hasChangedToCardImage"
                                    class="text-slate-500 dark:text-slate-400 text-xs"
                                >
                                    {{ cardName.substring(0, 4) }}
                                </span>
                            </div>
                        </div>

                        <div
                            class="flex flex-col items-start gap-4 self-stretch"
                        >
                            <h3
                                class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6"
                            >
                                Details
                            </h3>

                            <div class="w-full">
                                <UiInput
                                    id="previous-card"
                                    name="previous-card"
                                    label="Previous card"
                                    :error="errors.previousCard"
                                >
                                    <template #input>
                                        <CardSearch
                                            @update:modelValue="
                                                selectPreviousCard
                                            "
                                            :showInstitution="false"
                                            :cardToOmit="cardData"
                                            :modelValue="
                                                productChangeData.previous_card
                                            "
                                            :groupByInstitution="false"
                                        />
                                    </template>
                                </UiInput>
                            </div>

                            <div class="flex flex-col md:flex-row w-full gap-4">
                                <div v-if="showDateOpened" class="flex-1">
                                    <UiInput
                                        id="product_changed_date_opened"
                                        name="product_changed_date_opened"
                                        label="Date Opened"
                                        :error="
                                            errors.product_changed_date_opened
                                        "
                                        :options="{
                                            icon: 'calendar',
                                            wrapperClass: 'w-full',
                                        }"
                                    >
                                        <template #input>
                                            <flat-pickr
                                                id="product_changed_date_opened"
                                                :config="dateConfig"
                                                class="form-input date-picker flatpickr-input active border shadow-sm !pr-9 !pl-10 w-full validate"
                                                v-model="
                                                    productChangeData.product_changed_date_opened
                                                "
                                                placeholder="Select Date (MM/DD/YYYY)"
                                            ></flat-pickr>
                                        </template>
                                    </UiInput>
                                </div>
                                <div class="flex-1">
                                    <UiInput
                                        id="product_changed_date"
                                        name="product_changed_date"
                                        label="Date product changed"
                                        :error="errors.product_changed_date"
                                        :options="{
                                            icon: 'calendar',
                                            wrapperClass: 'w-full',
                                        }"
                                    >
                                        <template #input>
                                            <flat-pickr
                                                id="date-product-changed"
                                                :config="dateConfig"
                                                class="form-input flatpickr-input active border shadow-sm !pr-9 !pl-10 w-full validate"
                                                :class="{
                                                    'border-red-500':
                                                        errors.dateProductChanged,
                                                    'border-slate-200 dark:border-slate-700':
                                                        !errors.dateProductChanged,
                                                }"
                                                v-model="
                                                    productChangeData.product_changed_date
                                                "
                                                placeholder="Select Date (MM/DD/YYYY)"
                                            ></flat-pickr>
                                        </template>
                                    </UiInput>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- end step 2, add original card -->
                    <!-- step 2, change to new card -->
                    <div
                        v-show="
                            currentStep === 2 &&
                            selectedOption === 'change-to-new'
                        "
                        class="flex flex-col items-start gap-8 self-stretch w-full"
                    >
                        <!-- Card Visualization - Reverse order compared to first option -->
                        <div class="flex items-center gap-3 self-stretch mt-2">
                            <!-- Original Card (Card Art) - First -->
                            <div
                                class="flex h-[50px] w-[80px] items-center justify-center rounded overflow-hidden"
                                :style="
                                    cardData &&
                                    cardData.card &&
                                    cardData.card.card_image
                                        ? `background: url('${cdnUrl}/${cardData.card.card_image.path}/_small/${cardData.card.card_image.file_name}') lightgray 50% / cover no-repeat;`
                                        : cardImageUrl &&
                                            cardImageUrl.length > 0
                                          ? `background: url('${cardImageUrl}') lightgray 50% / cover no-repeat;`
                                          : 'background: lightgray;'
                                "
                            >
                                <span
                                    v-if="
                                        !(
                                            cardData &&
                                            cardData.card &&
                                            cardData.card.card_image
                                        ) &&
                                        !(
                                            cardImageUrl &&
                                            cardImageUrl.length > 0
                                        )
                                    "
                                    class="text-slate-500 dark:text-slate-400 text-xs"
                                >
                                    {{ cardName.substring(0, 4) }}
                                </span>
                            </div>

                            <!-- Arrow - Middle -->
                            <div
                                class="flex w-6 h-6 justify-center items-center"
                            >
                                <Icon icon="arrow-right-alt" />
                            </div>

                            <!-- SVG Card Design - Last -->
                            <div
                                v-if="productChangeData.new_card"
                                class="flex h-[50px] w-[80px] items-center justify-center rounded overflow-hidden"
                                :style="`background: url('${cdnUrl}/${productChangeData.new_card.card_image.path}/_small/${productChangeData.new_card.card_image.file_name}') lightgray 50% / cover no-repeat;`"
                            ></div>
                            <WalletIcon
                                v-else
                                icon="generic-card"
                                :width="82"
                                :height="50"
                                class="border-2 border-red-500 rounded"
                            />
                        </div>

                        <!-- Details Form -->
                        <div
                            class="flex flex-col items-start gap-4 self-stretch"
                        >
                            <h3
                                class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6"
                            >
                                Details
                            </h3>

                            <div
                                class="flex flex-col items-start gap-6 self-stretch"
                            >
                                <!-- New Card Input -->
                                <div class="flex flex-col items-start w-full">
                                    <label
                                        class="flex pb-2 items-start gap-2.5 self-stretch text-slate-600 dark:text-slate-300 text-sm font-medium leading-5"
                                    >
                                        New card
                                    </label>

                                    <CardSearch
                                        @update:modelValue="selectNewCard"
                                        :showInstitution="false"
                                        :modelValue="productChangeData.new_card"
                                        :groupByInstitution="false"
                                    />
                                </div>

                                <!-- Date Input -->
                                <div
                                    class="flex flex-col items-start self-stretch"
                                >
                                    <UiInput
                                        id="product_changed_date"
                                        name="product_changed_date"
                                        label="Date product changed"
                                        placeholder="Select Date (MM/DD/YYYY)"
                                        :error="errors.dateProductChanged"
                                        :options="{
                                            icon: 'calendar',
                                            wrapperClass: 'w-full',
                                        }"
                                    >
                                        <template #input>
                                            <flat-pickr
                                                id="date-product-changed"
                                                :config="dateConfig"
                                                class="form-input date-picker flatpickr-input active border shadow-sm !pr-9 !pl-10 w-full validate"
                                                v-model="
                                                    productChangeData.product_changed_date
                                                "
                                                placeholder="Select Date (MM/DD/YYYY)"
                                            ></flat-pickr>
                                        </template>
                                    </UiInput>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <!-- end step 2, change to new card -->
        <!-- errors -->
        <template #errors>
            <div class="w-full" v-show="errorsList.length > 0">
                <Errors :errors="errorsList" />
            </div>
        </template>
        <!-- end errors -->
        <!-- Footer -->
        <template #footer>
            <footer class="flex justify-between items-center gap-2">
                <!-- Left Side -->
                <div class="flex items-center gap-2">
                    <button
                        v-if="currentStep > 1"
                        @click="currentStep--"
                        class="btn-base btn-hollow !ml-0"
                    >
                        Back
                    </button>
                </div>

                <!-- Step Indicator -->
                <div
                    class="sm:flex justify-center items-center gap-2 absolute left-1/2 transform -translate-x-1/2 hidden"
                >
                    <span
                        v-for="step in totalSteps"
                        :key="step"
                        :class="[
                            'block h-1 rounded',
                            step === currentStep
                                ? 'w-6 bg-slate-800'
                                : 'w-6 bg-slate-200',
                        ]"
                    ></span>
                </div>

                <!-- Right Side -->
                <div class="flex items-center gap-2">
                    <button
                        type="button"
                        class="btn-base btn-hollow"
                        @click="handleCancel"
                    >
                        Cancel
                    </button>
                    <button
                        v-if="currentStep === 1"
                        type="button"
                        class="btn-base btn-dark"
                        @click="goToNextStep"
                    >
                        Next
                    </button>
                    <button
                        v-else
                        type="button"
                        class="btn-base btn-dark"
                        @click="validateAndSubmit"
                    >
                        Add product change
                    </button>
                </div>
            </footer>
        </template>
    </UiModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { mapActions, mapState } from 'pinia';
import { useWalletStore } from '@/store/wallet';
import { useCardsStore } from '@/store/cards';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';

import { vMaska } from 'maska/vue';
import { vClickOutside } from 'click-outside-vue3';
import Errors from '@/components/errors.vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import Icon from '@/components/ui/icon/icon.vue';
import WalletIcon from '@/components/wallet/wallet-icon.vue';
import CardSearch from '@/components/ui/card-search.vue';
import WelcomeBonusForm from '@/components/wallet/partials/welcome-bonus-form.vue';

const emit = defineEmits([
    'close',
    'select-option',
    'update:bonusAmount',
    'update:bonusReceived',
    'update:dateReceived',
    'update:minimumSpendDeadline',
    'submit',
    'reset-bonuses',
]);

const productChanges = defineModel('product-changes', {
    type: Array,
});

const productChangeCard = defineModel('product-change-card', {
    type: Object,
    default: () => ({
        product_changed_date_opened: '',
        product_changed_date: '',
        date_opened: '',
    }),
});

const productHistory = computed(() => {
    const arr = [...(props.cardData.product_changes || [])];

    if (
        productChangeData.value.product_changed_date &&
        productChangeData.value.previous_card
    ) {
        arr.push(productChangeData.value);
    }
    return arr.sort(
        (a, b) =>
            new Date(b.product_changed_date) - new Date(a.product_changed_date)
    );
});

const productChangeData = ref({
    previous_card: null,
    new_card: null,
    product_changed_date_opened: '',
    product_changed_date: '',
});

const selectedOption = defineModel('selected-option', {
    type: String,
    default: '',
});

const welcomeBonus = defineModel('welcome-bonus', {
    type: Object,
    default: () => ({}),
});

const tieredBonuses = defineModel('tiered-bonuses', {
    type: Array,
    default: () => [],
});

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    cardName: {
        type: String,
        default: 'Current Card',
    },
    cardData: {
        type: Object,
        default: () => ({}),
    },
    editingProductChange: {
        type: Object,
        default: null,
    },
    editingProductChangeIndex: {
        type: Number,
        default: null,
    },
    changedToCard: {
        type: Object,
        default: null,
    },
    isOriginalProductChange: {
        type: Boolean,
        default: false,
    },
});
const walletStore = useWalletStore();
const cardsStore = useCardsStore();
const currentStep = ref(1);
const totalSteps = ref(2);
const isEditing = computed(() => !!props.editingProductChange);

// Check if the Date Opened field should be shown
// Only show for the original (oldest) product change which involves the original card
const showDateOpened = computed(() => {
    const productChanges = props.cardData?.product_changes || [];
    const anyHasProductChangedDateOpened = productChanges.some(
        (pc) => pc.product_changed_date_opened
    );

    if (isEditing.value) {
        // Show if this is the original product change, OR if no product changes have product_changed_date_opened yet
        return props.isOriginalProductChange || !anyHasProductChangedDateOpened;
    }
    // If adding a new product change, hide if any existing one has a product_changed_date_opened
    return !anyHasProductChangedDateOpened;
});

// Get the card image for the "changed-to" card
// When editing, use the passed changedToCard; otherwise use the current card
const changedToCardImage = computed(() => {
    if (isEditing.value && props.changedToCard?.card_image) {
        return `${cdnUrl.value}/${props.changedToCard.card_image.path}/_small/${props.changedToCard.card_image.file_name}`;
    }
    if (props.cardData?.card?.card_image) {
        return `${cdnUrl.value}/${props.cardData.card.card_image.path}/_small/${props.cardData.card.card_image.file_name}`;
    }
    return cardImageUrl.value;
});

// Check if we have a valid changed-to card image
const hasChangedToCardImage = computed(() => {
    if (isEditing.value && props.changedToCard?.card_image) {
        return true;
    }
    if (props.cardData?.card?.card_image) {
        return true;
    }
    return cardImageUrl.value && cardImageUrl.value.length > 0;
});

// Check if welcome bonus section should be shown (only for original product change)
const showWelcomeBonusSection = computed(() => {
    if (isEditing.value) {
        return props.isOriginalProductChange;
    }
    // When adding new, show if no existing product changes have date_opened
    const productChanges = props.cardData?.product_changes || [];
    return !productChanges.some((pc) => pc.product_changed_date_opened);
});

const newCard = ref('');
const dateProductChanged = ref('');
const bonusType = ref('');
const spendDeadline = ref('');

const dateConfig = {
    dateFormat: 'm/d/Y',
    maxDate: 'today',
};
const futureDateConfig = {
    dateFormat: 'm/d/Y',
    minDate: 'today',
};

const errors = ref({
    previousCard: null,
    newCard: null,
    selectedOption: null,
    dateOpened: null,
    dateProductChanged: null,
});
const errorsList = ref([]);

const cdnUrl = computed(() => window.cdnUrl);

const cardImageUrl = computed(() => {
    if (props.cardData?.card?.card_image) {
        return `${cdnUrl.value}/${props.cardData.card.card_image.path}/_small/${props.cardData.card.card_image.file_name}`;
    }
    return '';
});

const modalSubTitle = computed(() => {
    if (currentStep.value === 1) {
        return 'Specify product change type';
    }
    return 'Specify product change details';
});

const localBonusReceived = computed({
    get: () => props.bonusReceived,
    set: (value) => emit('update:bonusReceived', value),
});

const localDateReceived = computed({
    get: () => props.dateReceived,
    set: (value) => emit('update:dateReceived', value),
});

watch(
    () => productChangeData.value.product_changed_date,
    (newVal) => {
        if (newVal) {
            errors.value.dateProductChanged = null;
            errorsList.value = errorsList.value.filter(
                (error) => error.message !== 'Select date'
            );
        }
    }
);

watch(
    () => productChangeData.value.product_changed_date_opened,
    (newVal) => {
        if (newVal) {
            errors.value.dateOpened = null;
            errorsList.value = errorsList.value.filter(
                (error) => error.message !== 'Select date'
            );
        }
    }
);

watch(
    () => productChangeData.value,
    (newVal) => {
        if (newVal) {
            errors.value.previousCard = null;
            errorsList.value = errorsList.value.filter(
                (error) => error.message !== 'Select card to continue'
            );
        }
    }
);

watch(selectedOption, (newVal) => {
    if (newVal) {
        errors.value.selectedOption = null;
        errorsList.value = errorsList.value.filter(
            (error) =>
                error.message !== 'Select product change type to continue'
        );
    }
});

watch(
    () => props.editingProductChange,
    (newVal, oldVal) => {
        if (newVal) {
            // Pre-populate form with existing product change data
            productChangeData.value = {
                previous_card: newVal.previous_card || null,
                new_card: null,
                product_changed_date_opened:
                    newVal.product_changed_date_opened || '',
                product_changed_date: newVal.product_changed_date || '',
            };
            selectedOption.value = 'add-previous';
            currentStep.value = 2;
        } else if (oldVal) {
            // Only reset form when transitioning from editing to not editing
            productChangeData.value = {
                previous_card: null,
                new_card: null,
                product_changed_date_opened: '',
                product_changed_date: '',
            };
            currentStep.value = 1;
        }
    },
    { immediate: true }
);

const loadWalletData = async () => {
    if (!walletStore.wallet || !walletStore.wallet.wallet_users) {
        await walletStore.getWallet();
    }
};

const goToNextStep = () => {
    errors.value = {
        previousCard: null,
        newCard: null,
        selectedOption: null,
        dateOpened: null,
        dateProductChanged: null,
    };
    errorsList.value = [];

    if (!selectedOption.value) {
        errors.value.selectedOption = true;
        errorsList.value.push({
            message: 'Select product change type to continue',
        });
        return;
    }

    currentStep.value = 2;
};

const handleCancel = () => {
    selectedOption.value = '';
    emit('close');
};

const validateAndSubmit = () => {
    errors.value = {
        previousCard: null,
        newCard: null,
        selectedOption: null,
        dateOpened: null,
        dateProductChanged: null,
    };
    errorsList.value = [];

    let hasErrors = false;

    if (selectedOption.value === 'add-previous') {
        if (!productChangeData.value.previous_card) {
            errors.value.previousCard = 'Select card to continue';
            errorsList.value.push({
                message: 'Select card to continue',
            });
            hasErrors = true;
        }
        if (
            showDateOpened.value &&
            !productChangeData.value.product_changed_date_opened
        ) {
            errors.value.product_changed_date_opened =
                'Select date previous card was opened';
            errorsList.value.push({
                message: 'Select date previous card was opened',
            });
            hasErrors = true;
        }
        if (!productChangeData.value.product_changed_date) {
            errors.value.product_changed_date = 'Select date card was changed';
            errorsList.value.push({
                message: 'Select date card was changed',
            });
            hasErrors = true;
        }
    } else if (selectedOption.value === 'change-to-new') {
        if (!productChangeData.value.new_card) {
            errors.value.newCard = 'Select card to continue';
            errorsList.value.push({
                message: 'Select card to continue',
            });
            hasErrors = true;
        }

        if (!productChangeData.value.product_changed_date) {
            errors.value.dateProductChanged = 'Select date';
            errorsList.value.push({
                message: 'Select date',
            });
            hasErrors = true;
        }
    }

    if (hasErrors) {
        return;
    }

    submitProductChange();
};

const submitProductChange = () => {
    let cardData;
    if (selectedOption.value === 'add-previous') {
        cardData = productChangeData.value.previous_card;
    } else if (selectedOption.value === 'change-to-new') {
        cardData = productChangeData.value.new_card;
    }

    // Include the card data, selected option, and date information
    const submissionData = {
        ...cardData,
        selectedOption: selectedOption.value,
        product_changed_date: productChangeData.value.product_changed_date,
        product_changed_date_opened:
            productChangeData.value.product_changed_date_opened,
        isEditing: isEditing.value,
        editingProductChangeId: props.editingProductChange?.id || null,
    };

    emit('submit', submissionData);

    emit('close');
};

const selectPreviousCard = (card) => {
    if (!card) {
        productChangeData.value.previous_card = null;
        return;
    }
    productChangeData.value.previous_card = card;

    errors.value.previousCard = null;
};

const selectNewCard = (card) => {
    if (!card) {
        productChangeData.value.new_card = null;
        return;
    }
    productChangeData.value.new_card = card;
    errors.value.newCard = null;
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
